// RiskManagement.jsx
import React, { Fragment, useMemo, useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Ban,
  BrainCircuit,
  Check,
  ChevronRight,
  Database,
  FileCheck2,
  Fingerprint,
  Gauge,
  Globe2,
  KeyRound,
  Layers3,
  LockKeyhole,
  MapPin,
  Server,
  Settings2,
  ShieldCheck,
  TimerReset,
  UserX,
  Users,
  WifiOff,
  X,
} from "lucide-react";

import "./Styles.css";
import HeaderToMainPage from "../../Components/Universal/Header/Index";
import Footer from "../../Components/Universal/Footer/Index";
import { Link } from "react-router-dom";

const riskSurfaces = [
  {
    id: "wager", number: "01", accent: "cyan", icon: Gauge,
    kicker: "WAGER & EXPOSURE LIMITS",
    title: "Control the exposure a player can create.",
    summary: "Set maximum stakes and game-specific operating limits before a wager is accepted.",
    body: "Ethosino checks configured wager restrictions on the server, where the game request is authorized and processed. An attempted stake outside the permitted range is rejected instead of relying on a disabled button or a frontend warning. Operators can review the permitted limits alongside game and account activity when investigating unusual betting patterns.",
    bullets: [
      "Operator-configured maximum stake values",
      "Game and category-level limit settings",
      "Validation before authoritative round admission",
      "Rejected wager attempts available for operational investigation where logged",
    ],
  },
  {
    id: "self-exclusion", number: "02", accent: "pink", icon: UserX,
    kicker: "PLAYER PROTECTION",
    title: "Honor exclusion at the account boundary.",
    summary: "An active exclusion changes whether an account can access protected play.",
    body: "Responsible-gaming requests should not depend on a support agent manually finding every open session. Ethosino's account eligibility checks can prevent excluded players from continuing protected activity and keep the relevant status visible for authorized support and risk review. Changes to exclusion status require an accountable process rather than an informal exception.",
    bullets: [
      "Player-accessible exclusion requests",
      "Account-status checks on protected game routes",
      "Restricted access while an exclusion is active",
      "Reviewable account and intervention history",
    ],
  },
  {
    id: "geo", number: "03", accent: "green", icon: MapPin,
    kicker: "JURISDICTION CONTROLS",
    title: "Decide where play is permitted, not merely where traffic comes from.",
    summary: "Compare available location evidence with the access policy defined for that deployment.",
    body: "Device coordinates, IP-derived location and the operator's jurisdiction configuration serve different purposes. Ethosino uses server-side checks to determine whether protected access can continue, including cases where a player denies location permission or appears to be in a restricted region. The policy should reflect the operator's legal review; software does not create permission to operate.",
    bullets: [
      "Configured permitted and restricted regions",
      "Device-location and IP-location context",
      "Explicit handling of unavailable location evidence",
      "Denied access and retry or support pathways",
    ],
  },
  {
    id: "vpn", number: "04", accent: "violet", icon: WifiOff,
    kicker: "NETWORK & ACCESS SIGNALS",
    title: "Flag suspicious access without pretending every signal is proof.",
    summary: "VPN, proxy and inconsistent location signals inform the access decision and possible review.",
    body: "An anonymizing network can indicate attempted jurisdiction evasion, but network classification can also be wrong. Ethosino allows relevant network indicators to be assessed against tenant policy alongside the player's other location evidence. Operators can distinguish an outright access restriction from an event that warrants investigation, rather than treating every flagged connection as confirmed misconduct.",
    bullets: [
      "VPN and proxy detection indicators",
      "IP and device-location consistency context",
      "Policy-based restrictions or review handling",
      "Access decisions linked to security signals",
    ],
  },
  {
    id: "session", number: "05", accent: "amber", icon: TimerReset,
    kicker: "SESSION & BEHAVIOR EXPOSURE",
    title: "Look beyond the size of a single bet.",
    summary: "Session length and repeated activity can reveal exposure that a per-wager cap will miss.",
    body: "Operational risk can build through frequent wagers, unusually long sessions or repeated activity after warnings. Tenant-level session policies and cumulative thresholds provide additional ways to restrict or flag this behavior. Game records, session history and player context give staff a basis for reviewing patterns instead of making decisions from one isolated transaction.",
    bullets: [
      "Session duration and activity thresholds",
      "Cumulative play and wager-frequency context",
      "Concurrent-session restrictions where configured",
      "Operational escalation for concerning patterns",
    ],
  },
  {
    id: "governance", number: "06", accent: "blue", icon: FileCheck2,
    kicker: "INVESTIGATION & AUDIT",
    title: "Show the evidence behind a risk decision.",
    summary: "Connect operator interventions, policy changes and relevant events to a reviewable history.",
    body: "A risk alert becomes useful when staff can determine which account was involved, what triggered the rule, which action followed and who authorized a later change. Ethosino's administrative risk and audit surfaces bring together relevant player, transaction, access and configuration context. The objective is a defensible investigation trail, not merely a dashboard full of warning icons.",
    bullets: [
      "Risk flags, incident context and review queues",
      "Administrative actions attributed to authorized staff",
      "Related player, wallet and access records",
      "Configuration and decision history for follow-up",
    ],
  },
];

const selfExclusionSteps = [
  ["01", UserX, "Player requests a restriction", "An accessible account flow lets the player initiate an exclusion without first needing a staff member to approve the request."],
  ["02", KeyRound, "Account eligibility updates", "The active restriction becomes account state used by protected gameplay and access checks."],
  ["03", Ban, "Further play is blocked", "Protected wager attempts are denied while the restriction applies, including requests made outside the visible interface."],
  ["04", FileCheck2, "Staff can review the record", "Authorized reviewers can inspect the status and related events when responding to an account question or escalation."],
];

const tenantProfiles = [
  {
    id: "conservative", label: "LIMITED PILOT", title: "Restrict exposure during early testing",
    maxBet: "$25", session: "45 min", geo: "strict", vpn: "block / review", exclusion: "automatic",
    note: "Illustrative values only: a small pilot may choose lower stakes and shorter sessions while checking eligibility decisions, event capture and manual review procedures.",
  },
  {
    id: "balanced", label: "OPERATING REVIEW", title: "Wider limits with active oversight",
    maxBet: "$100", session: "90 min", geo: "required", vpn: "risk based", exclusion: "automatic",
    note: "Example of a different operating envelope. Higher limits should follow tested policy, staff oversight and evidence that the operator can investigate access and account events reliably.",
  },
  {
    id: "custom", label: "OPERATOR POLICY", title: "Define rules for the intended market",
    maxBet: "per game", session: "policy", geo: "jurisdiction map", vpn: "tenant rule", exclusion: "automatic",
    note: "An operator can define the values appropriate to its product, jurisdiction and responsible-gaming obligations. These labels are configuration illustrations, not automatic legal recommendations.",
  },
];

const principles = [
  [Server, "Validate on the server", "A limit must hold when a request reaches the API directly, not only when the browser displays the correct controls."],
  [Layers3, "Keep policies attributable", "A risk decision needs the relevant operator configuration and account context so staff can explain why the request was accepted or denied."],
  [Fingerprint, "Keep risk separate from results", "Eligibility and exposure checks belong before round acceptance. Once admitted, the game resolves under its applicable game rules and fairness model."],
  [FileCheck2, "Preserve the decision trail", "Capture the significant event, affected account, applied policy and authorized intervention where those records are available."],
  [BrainCircuit, "Use AI to investigate, not adjudicate", "AI-assisted summaries may help find unusual trends in permitted data; they do not approve withdrawals, change balances or impose irreversible account decisions."],
  [LockKeyhole, "Make review proportional", "An access warning, an exclusion and a payout flag require different responses. Controls should permit targeted investigation rather than blanket assumptions."],
];

const tickerItems = [
  "OPERATOR EXPOSURE LIMITS",
  "PLAYER SAFETY & SELF-EXCLUSION",
  "LOCATION ELIGIBILITY DECISIONS",
  "NETWORK-RISK INVESTIGATIONS",
  "SESSION ACTIVITY REVIEW",
  "WITHDRAWAL & TREASURY VISIBILITY",
  "INCIDENT QUEUES & AUDIT HISTORY",
  "ACCOUNTABLE POLICY CHANGES",
];

const Reveal = ({ children, className = "", direction = "up", delay = 0 }) => {
  const reduceMotion = useReducedMotion();
  const starts = {
    left: { x: -120, y: 0, rotate: -1.6 },
    right: { x: 120, y: 0, rotate: 1.6 },
    up: { x: 0, y: 74, rotate: 0 },
    topLeft: { x: -150, y: -110, rotate: -2.6 },
    topRight: { x: 150, y: -110, rotate: 2.6 },
    bottomLeft: { x: -150, y: 112, rotate: -2.6 },
    bottomRight: { x: 150, y: 112, rotate: 2.6 },
  };
  const start = starts[direction] || starts.up;

  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : { opacity: 0, ...start, scale: 0.95, filter: "blur(10px)" }}
      whileInView={{ opacity: 1, x: 0, y: 0, rotate: 0, scale: 1, filter: "blur(0px)" }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: reduceMotion ? 0 : 0.84, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Spotlight = ({ children, className = "" }) => {
  const handlePointerMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty("--erm13-mx", `${event.clientX - rect.left}px`);
    event.currentTarget.style.setProperty("--erm13-my", `${event.clientY - rect.top}px`);
  };

  return (
    <div className={`eth-risk-v13-spotlight ${className}`} onPointerMove={handlePointerMove}>
      <div className="eth-risk-v13-spotlight-glow" />
      {children}
    </div>
  );
};

const RiskGate = ({ reduceMotion }) => {
  const nodes = [
    ["WAGER LIMIT", Gauge, "cyan"],
    ["EXCLUSION", UserX, "pink"],
    ["REGION CHECK", MapPin, "green"],
    ["NETWORK FLAG", WifiOff, "violet"],
  ];

  return (
    <div className="eth-risk-v13-gate">
      <div className="eth-risk-v13-gate-grid" />
      <div className="eth-risk-v13-gate-scan" />
      <div className="eth-risk-v13-gate-top">
        <div><span className="eth-risk-v13-live-dot" /> RISK CHECK / ILLUSTRATIVE</div>
        <b>SERVER DECISION</b>
      </div>

      <div className="eth-risk-v13-gate-field">
        <motion.div
          className="eth-risk-v13-gate-ring eth-risk-v13-gate-ring--outer"
          animate={reduceMotion ? {} : { rotate: 360 }}
          transition={{ duration: 34, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="eth-risk-v13-gate-ring eth-risk-v13-gate-ring--inner"
          animate={reduceMotion ? {} : { rotate: -360 }}
          transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
        />

        <div className="eth-risk-v13-gate-core">
          <span className="eth-risk-v13-gate-wave" />
          <ShieldCheck size={30} strokeWidth={1.6} />
          <small>RISK POLICIES</small>
          <strong>CHECK REQUEST</strong>
          <b>BEFORE GAMEPLAY</b>
        </div>

        {nodes.map((node, index) => {
          const Icon = node[1];
          return (
            <motion.div
              key={node[0]}
              className={`eth-risk-v13-gate-node eth-risk-v13-gate-node--${index + 1} eth-risk-v13-gate-node--${node[2]}`}
              animate={reduceMotion ? {} : { y: [0, index % 2 === 0 ? -8 : 8, 0], x: [0, index < 2 ? 5 : -5, 0] }}
              transition={{ duration: 4.6 + index * 0.35, repeat: Infinity, ease: "easeInOut" }}
            >
              <span><Icon size={16} /></span>
              <div><small>CHECK 0{index + 1}</small><strong>{node[0]}</strong></div>
            </motion.div>
          );
        })}

        <span className="eth-risk-v13-gate-packet eth-risk-v13-gate-packet--one" />
        <span className="eth-risk-v13-gate-packet eth-risk-v13-gate-packet--two" />
        <span className="eth-risk-v13-gate-packet eth-risk-v13-gate-packet--three" />
      </div>

      <div className="eth-risk-v13-gate-bottom">
        <div><span>REQUEST</span><strong>PLAYER ACTION</strong></div>
        <ArrowRight size={16} />
        <div><span>DECISION</span><strong>DECISION RECORDED</strong></div>
        <b>OPERATOR CONTEXT</b>
      </div>
    </div>
  );
};

const RiskManagement = () => {
  const reduceMotion = useReducedMotion();
  const [activeSurface, setActiveSurface] = useState(0);
  const [activeProfile, setActiveProfile] = useState(0);
  const currentSurface = riskSurfaces[activeSurface];
  const CurrentSurfaceIcon = currentSurface.icon;
  const currentProfile = tenantProfiles[activeProfile];
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, restDelta: 0.001 });
  const heroY = useTransform(scrollYProgress, [0, 0.16], [0, reduceMotion ? 0 : 72]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.13], [1, 0.77]);
  const heroMetrics = useMemo(() => [
    ["EXPOSURE", "stake and session limits"],
    ["ACCESS", "location and network checks"],
    ["REVIEW", "risk signals and investigations"],
    ["EVIDENCE", "operator activity history"],
  ], []);



  return (
    <Fragment>
      <motion.div className="eth-risk-v13-scroll-progress" style={{ scaleX: progress }} />
      <HeaderToMainPage />

      <main className="eth-risk-v13-page eth-risk-v13-readable" id="ethosino-risk-management-v13">
        <div className="eth-risk-v13-atmosphere" aria-hidden="true">
          <div className="eth-risk-v13-atmosphere-grid" />
          <div className="eth-risk-v13-atmosphere-noise" />
          <div className="eth-risk-v13-orb eth-risk-v13-orb--cyan" />
          <div className="eth-risk-v13-orb eth-risk-v13-orb--green" />
          <div className="eth-risk-v13-orb eth-risk-v13-orb--violet" />
          <div className="eth-risk-v13-orb eth-risk-v13-orb--pink" />
        </div>

        <section style={{ marginTop: 50 }} className="eth-risk-v13-hero">
          <motion.div className="eth-risk-v13-hero-parallax" style={{ y: heroY, opacity: heroOpacity }}>
            <div className="eth-risk-v13-shell eth-risk-v13-hero-grid">
              <div className="eth-risk-v13-hero-copy">
                <motion.div className="eth-risk-v13-system-badge" initial={reduceMotion ? false : { opacity: 0, x: -38 }} animate={{ opacity: 1, x: 0 }}>
                  <span className="eth-risk-v13-live-dot" /> RISK & EXPOSURE CONTROLS <i /> AUDITABLE DECISIONS
                </motion.div>

                <motion.h1
                  initial={reduceMotion ? false : { opacity: 0, x: -115, y: 46, rotate: -2 }}
                  animate={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
                  transition={{ duration: 0.88, delay: 0.04, ease: [0.16, 1, 0.3, 1] }}
                >
                  Protect the operation. <span>Understand every risk decision.</span>
                </motion.h1>

                <motion.p className="eth-risk-v13-hero-lead" initial={reduceMotion ? false : { opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}>
                  From a rejected wager to a flagged connection or account restriction, Ethosino gives operators controls to manage exposure and the operational context to understand what happened. Review activity, apply configured restrictions and investigate significant events through connected risk and audit surfaces.
                </motion.p>

                <motion.div className="eth-risk-v13-hero-thesis" initial={reduceMotion ? false : { opacity: 0, x: -46 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.24 }}>
                  <ShieldCheck size={18} />
                  <span>Risk controls should <strong>stop disallowed actions and leave a reviewable record.</strong> Operators need both prevention and an explanation when a decision is challenged.</span>
                </motion.div>

                <motion.div className="eth-risk-v13-hero-actions" initial={reduceMotion ? false : { opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.31 }}>
                  <a href="#risk-surfaces" className="eth-risk-v13-btn eth-risk-v13-btn--primary">Inspect the control categories <ArrowRight size={17} /></a>
                  <Link to="/book-demo" className="eth-risk-v13-btn eth-risk-v13-btn--ghost">Request a risk walkthrough</Link>
                </motion.div>

                <div className="eth-risk-v13-hero-proofline">
                  <span><Gauge size={14} /> exposure limits</span>
                  <span><UserX size={14} /> player safety</span>
                  <span><MapPin size={14} /> access decisions</span>
                  <span><WifiOff size={14} /> network flags</span>
                  <span><Layers3 size={14} /> event review</span>
                </div>
              </div>

              <motion.div className="eth-risk-v13-hero-visual" initial={reduceMotion ? false : { opacity: 0, x: 120, y: -64, rotate: 2.7, scale: 0.94 }} animate={{ opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 }} transition={{ duration: 0.98, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}>
                <RiskGate reduceMotion={reduceMotion} />
                <motion.div className="eth-risk-v13-float eth-risk-v13-float--one" animate={reduceMotion ? {} : { y: [0, -12, 0], rotate: [-1, 1, -1] }} transition={{ duration: 5.6, repeat: Infinity, ease: "easeInOut" }}>
                  <Ban size={15} /><div><span>DISALLOWED WAGER</span><strong>REJECTED BY POLICY</strong></div>
                </motion.div>
                <motion.div className="eth-risk-v13-float eth-risk-v13-float--two" animate={reduceMotion ? {} : { y: [0, 11, 0], rotate: [1, -1, 1] }} transition={{ duration: 6.4, repeat: Infinity, ease: "easeInOut" }}>
                  <FileCheck2 size={15} /><div><span>ADMIN ACTIVITY</span><strong>REVIEWABLE</strong></div>
                </motion.div>
              </motion.div>
            </div>

            <div className="eth-risk-v13-shell">
              <motion.div className="eth-risk-v13-metric-strip" initial={reduceMotion ? false : { opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.42 }}>
                {heroMetrics.map((item, index) => (
                  <div key={item[0]} className="eth-risk-v13-metric"><span>0{index + 1} / {item[0]}</span><strong>{item[1]}</strong></div>
                ))}
              </motion.div>
            </div>
          </motion.div>

          <div className="eth-risk-v13-ticker"><div className="eth-risk-v13-ticker-track">
            {[...tickerItems, ...tickerItems].map((item, index) => <React.Fragment key={`${item}-${index}`}><span>{item}</span><i>◆</i></React.Fragment>)}
          </div></div>
        </section>

        <nav className="eth-risk-v13-subnav">
          <div className="eth-risk-v13-shell eth-risk-v13-subnav-inner">
            <div className="eth-risk-v13-subnav-brand"><span /> RISK OPERATIONS / EVIDENCE</div>
            <div className="eth-risk-v13-subnav-links">
              <a href="#risk-surfaces">Risk controls</a>
              <a href="#server-authority">Request checks</a>
              <a href="#self-exclusion">Player safety</a>
              <a href="#tenant-posture">Policy examples</a>
              <a href="#governance">Audit history</a>
              <Link className="eth-risk-v13-subnav-cta" to="/book-demo">Demo</Link>
            </div>
          </div>
        </nav>

        <section className="eth-risk-v13-section">
          <div className="eth-risk-v13-shell">
            <Reveal direction="topLeft" className="eth-risk-v13-section-head eth-risk-v13-section-head--split">
              <div><span className="eth-risk-v13-kicker">PREVENTION AND INVESTIGATION</span><h2>Risk management starts before play <span>and continues after the event.</span></h2></div>
              <p>Entry checks prevent disallowed activity from becoming gameplay or financial exposure. When something unusual still occurs, an operator needs the underlying event history, related player context and an accountable path to resolution.</p>
            </Reveal>

            <div className="eth-risk-v13-thesis-grid">
              <Reveal direction="left"><Spotlight className="eth-risk-v13-thesis-card eth-risk-v13-thesis-card--weak">
                <div className="eth-risk-v13-thesis-card-top"><X size={21} /><span>DISCONNECTED REVIEW</span></div>
                <h3>An alert without context creates more work.</h3>
                <p>A flagged session, failed withdrawal or unusual wager means little if the reviewer cannot see the originating account, relevant rule and related transaction or access activity.</p>
                <div className="eth-risk-v13-thesis-chain eth-risk-v13-thesis-chain--weak"><span>ALERT</span><ArrowRight size={13} /><span>GUESS</span><ArrowRight size={13} /><span>ESCALATE</span></div>
              </Spotlight></Reveal>
              <Reveal direction="right"><Spotlight className="eth-risk-v13-thesis-card eth-risk-v13-thesis-card--strong">
                <div className="eth-risk-v13-thesis-card-top"><Check size={21} /><span>CONTEXTUAL INVESTIGATION</span></div>
                <h3>Keep prevention and investigation connected.</h3>
                <p>Policy checks determine whether a protected request can proceed. The resulting operational records can then support support-ticket handling, risk investigations and the review of sensitive account decisions.</p>
                <div className="eth-risk-v13-thesis-chain"><span>CHECK</span><ArrowRight size={13} /><span>DECIDE</span><ArrowRight size={13} /><span>REVIEW</span></div>
              </Spotlight></Reveal>
            </div>

            <Reveal direction="bottomRight" className="eth-risk-v13-manifesto"><Server size={23} /><div><span>CONTROL AND ACCOUNTABILITY</span><strong>Apply rules where transactions are authorized. Explain decisions where people investigate.</strong></div></Reveal>
          </div>
        </section>

        <section className="eth-risk-v13-section eth-risk-v13-section--panel" id="risk-surfaces">
          <div className="eth-risk-v13-shell">
            <Reveal direction="topRight" className="eth-risk-v13-section-head eth-risk-v13-section-head--split">
              <div><span className="eth-risk-v13-kicker">SIX OPERATIONAL RISK AREAS</span><h2>Different risks require <span>different controls and evidence.</span></h2></div>
              <p>Select an area to see the operator-facing questions it addresses, the policy involved and the records a reviewer would need. These are distinct activities, not six descriptions of the same risk engine.</p>
            </Reveal>

            <div className="eth-risk-v13-surface-layout">
              <Reveal direction="left" className="eth-risk-v13-surface-tabs">
                {riskSurfaces.map((surface, index) => {
                  const Icon = surface.icon;
                  return (
                    <motion.button key={surface.id} type="button" className={`eth-risk-v13-surface-tab eth-risk-v13-surface-tab--${surface.accent} ${activeSurface === index ? "is-active" : ""}`} onClick={() => setActiveSurface(index)} whileHover={{ x: 5 }} whileTap={{ scale: 0.985 }}>
                      <span className="eth-risk-v13-surface-tab-index">{surface.number}</span>
                      <span className="eth-risk-v13-surface-tab-icon"><Icon size={18} /></span>
                      <span className="eth-risk-v13-surface-tab-copy"><small>{surface.kicker}</small><strong>{surface.title}</strong><em>{surface.summary}</em></span>
                      <ChevronRight size={16} />
                    </motion.button>
                  );
                })}
              </Reveal>

              <Reveal direction="right" className="eth-risk-v13-surface-detail-wrap">
                <AnimatePresence mode="wait">
                  <motion.article key={currentSurface.id} className={`eth-risk-v13-surface-detail eth-risk-v13-surface-detail--${currentSurface.accent}`} initial={reduceMotion ? false : { opacity: 0, x: 80, y: -28, rotate: 1.4, filter: "blur(8px)" }} animate={{ opacity: 1, x: 0, y: 0, rotate: 0, filter: "blur(0px)" }} exit={reduceMotion ? undefined : { opacity: 0, x: -62, y: 24, rotate: -1.2, filter: "blur(7px)" }} transition={{ duration: 0.34 }}>
                    <div className="eth-risk-v13-surface-detail-top"><span className="eth-risk-v13-surface-big-index">{currentSurface.number}</span><div className="eth-risk-v13-surface-detail-icon"><CurrentSurfaceIcon size={29} /></div></div>
                    <small>{currentSurface.kicker}</small>
                    <h3>{currentSurface.title}</h3>
                    <p className="eth-risk-v13-surface-summary">{currentSurface.summary}</p>
                    <p className="eth-risk-v13-surface-body">{currentSurface.body}</p>
                    <div className="eth-risk-v13-surface-bullets">{currentSurface.bullets.map((bullet) => <span key={bullet}><Check size={12} />{bullet}</span>)}</div>
                  </motion.article>
                </AnimatePresence>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="eth-risk-v13-section" id="server-authority">
          <div className="eth-risk-v13-shell">
            <Reveal direction="bottomLeft" className="eth-risk-v13-section-head">
              <span className="eth-risk-v13-kicker">REQUEST DECISION PATH</span>
              <h2>Follow a protected request <span>from account check to decision record.</span></h2>
              <p>A wager or restricted game request must be evaluated against the correct account and policy. The resulting decision should be traceable: what was requested, which restrictions applied and whether the action was admitted.</p>
            </Reveal>

            <Reveal direction="bottomRight" className="eth-risk-v13-server-path">
              <div className="eth-risk-v13-server-track" />
              {[
                [Users, "REQUEST", "wager or access attempt"],
                [Layers3, "POLICY", "configured limits"],
                [ShieldCheck, "ACCOUNT", "status + restrictions"],
                [MapPin, "ACCESS", "region + network"],
                [Gauge, "EXPOSURE", "stake + activity"],
                [Server, "DECISION", "accept or reject"],
                [Database, "REVIEW", "event history"],
              ].map((step, index) => {
                const Icon = step[0];
                return <div className="eth-risk-v13-server-node" key={step[1]}><div><Icon size={18} /></div><span>{step[1]}</span><strong>{step[2]}</strong></div>;
              })}
              <motion.span className="eth-risk-v13-server-packet" animate={reduceMotion ? {} : { left: ["1%", "97%"] }} transition={{ duration: 7, repeat: Infinity, ease: "linear" }} />
            </Reveal>

            <div className="eth-risk-v13-authority-grid">
              <Reveal direction="left"><Spotlight className="eth-risk-v13-authority-card"><div className="eth-risk-v13-authority-card-top"><Server size={20} /><span>AUTHORITATIVE SYSTEM</span></div><div className="eth-risk-v13-authority-list"><span><Check size={12} /> account eligibility</span><span><Check size={12} /> stake authorization</span><span><Check size={12} /> location restrictions</span><span><Check size={12} /> account restriction state</span><span><Check size={12} /> transaction and decision records</span></div></Spotlight></Reveal>
              <Reveal direction="right"><Spotlight className="eth-risk-v13-authority-card eth-risk-v13-authority-card--client"><div className="eth-risk-v13-authority-card-top"><Globe2 size={20} /><span>PLAYER INTERFACE</span></div><div className="eth-risk-v13-authority-list"><span><Check size={12} /> submit protected requests</span><span><Check size={12} /> provide location permission</span><span><Check size={12} /> show account restrictions</span><span><X size={12} /> override a denied request</span><span><X size={12} /> approve wallet settlement</span></div></Spotlight></Reveal>
            </div>
          </div>
        </section>

        <section className="eth-risk-v13-section eth-risk-v13-section--panel" id="self-exclusion">
          <div className="eth-risk-v13-shell">
            <Reveal direction="topRight" className="eth-risk-v13-section-head eth-risk-v13-section-head--split">
              <div><span className="eth-risk-v13-kicker">SELF-EXCLUSION WORKFLOW</span><h2>When a player opts out, <span>the restriction must follow the account.</span></h2></div>
              <p>An exclusion request should produce a clear change in account eligibility, not just a support note. The sequence below shows how the restriction affects protected activity and how staff can later review what occurred.</p>
            </Reveal>

            <div className="eth-risk-v13-exclusion-flow">
              <div className="eth-risk-v13-exclusion-track" />
              {selfExclusionSteps.map((step, index) => {
                const Icon = step[1];
                const dirs = ["topLeft", "topRight", "bottomLeft", "bottomRight"];
                return <Reveal key={step[0]} direction={dirs[index]} delay={index * 0.045}><Spotlight className="eth-risk-v13-exclusion-card"><div className="eth-risk-v13-exclusion-card-top"><span>{step[0]}</span><Icon size={21} /></div><h3>{step[2]}</h3><p>{step[3]}</p></Spotlight></Reveal>;
              })}
              <motion.span className="eth-risk-v13-exclusion-packet" animate={reduceMotion ? {} : { left: ["2%", "96%"] }} transition={{ duration: 6, repeat: Infinity, ease: "linear" }} />
            </div>

            <Reveal direction="right" className="eth-risk-v13-exclusion-callout"><UserX size={21} /><div><span>RESPONSIBLE-GAMING ACCOUNTABILITY</span><strong>Give players a direct way to restrict access, and make that status visible to the systems enforcing it.</strong></div></Reveal>
          </div>
        </section>

        <section className="eth-risk-v13-section">
          <div className="eth-risk-v13-shell">
            <Reveal direction="topLeft" className="eth-risk-v13-section-head">
              <span className="eth-risk-v13-kicker">ACCESS AND JURISDICTION REVIEW</span>
              <h2>Location checks need more than <span>a country returned by an IP lookup.</span></h2>
              <p>Operators need to know whether a player supplied location permission, whether the network location is consistent and whether the applicable jurisdiction is permitted. A missing or questionable signal should produce an explicit handling decision.</p>
            </Reveal>

            <div className="eth-risk-v13-geo-layout">
              <Reveal direction="left" className="eth-risk-v13-geo-visual">
                <div className="eth-risk-v13-geo-grid" />
                <div className="eth-risk-v13-geo-map-shape">
                  <span className="eth-risk-v13-geo-zone eth-risk-v13-geo-zone--allowed">ALLOWED</span>
                  <span className="eth-risk-v13-geo-zone eth-risk-v13-geo-zone--review">REVIEW</span>
                  <span className="eth-risk-v13-geo-zone eth-risk-v13-geo-zone--blocked">BLOCKED</span>
                  <motion.span className="eth-risk-v13-geo-player" animate={reduceMotion ? {} : { x: [0, 72, 35, 0], y: [0, -36, 58, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}><MapPin size={17} /></motion.span>
                </div>
                <div className="eth-risk-v13-geo-telemetry"><div><span>COORDINATES</span><strong>device context</strong></div><div><span>IP CONTEXT</span><strong>network estimate</strong></div><div><span>VPN / PROXY</span><strong>review indicator</strong></div></div>
              </Reveal>

              <Reveal direction="right" className="eth-risk-v13-geo-copy">
                <div className="eth-risk-v13-geo-card"><div className="eth-risk-v13-geo-card-head"><MapPin size={20} /><div><span>GEOFENCING</span><strong>Evaluate the requested region.</strong></div></div><p>Where available, device coordinates provide a stronger location signal than a country-level IP lookup. The backend compares the evidence with the regions the operator has enabled and handles unavailable permission explicitly.</p></div>
                <div className="eth-risk-v13-geo-card"><div className="eth-risk-v13-geo-card-head"><WifiOff size={20} /><div><span>VPN / PROXY SIGNALS</span><strong>Investigate inconsistent network evidence.</strong></div></div><p>An identified VPN, proxy or mismatch is a lead for a policy decision, not conclusive evidence of cheating. Depending on configuration, access may be restricted or referred for review.</p></div>
                <div className="eth-risk-v13-geo-principle"><AlertTriangle size={17} /><span>Location-denied and restricted-region states need explicit responses, including retries or support where appropriate.</span></div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="eth-risk-v13-section eth-risk-v13-section--panel" id="tenant-posture">
          <div className="eth-risk-v13-shell">
            <Reveal direction="bottomLeft" className="eth-risk-v13-section-head eth-risk-v13-section-head--split">
              <div><span className="eth-risk-v13-kicker">EXAMPLE EXPOSURE CONFIGURATIONS</span><h2>Operators can tune exposure <span>to their own launch requirements.</span></h2></div>
              <p>Compare example combinations of wager maximums, session durations and location responses. Dollar amounts and durations are illustrations—not recommended thresholds, automatic defaults or guarantees about a particular jurisdiction.</p>
            </Reveal>

            <div className="eth-risk-v13-profile-layout">
              <Reveal direction="left" className="eth-risk-v13-profile-tabs">
                {tenantProfiles.map((profile, index) => <motion.button key={profile.id} type="button" className={`eth-risk-v13-profile-tab ${activeProfile === index ? "is-active" : ""}`} onClick={() => setActiveProfile(index)} whileHover={{ x: 5 }}><span>0{index + 1}</span><div><small>{profile.label}</small><strong>{profile.title}</strong></div><ChevronRight size={15} /></motion.button>)}
              </Reveal>

              <Reveal direction="right" className="eth-risk-v13-profile-detail-wrap">
                <AnimatePresence mode="wait">
                  <motion.article key={currentProfile.id} className="eth-risk-v13-profile-detail" initial={reduceMotion ? false : { opacity: 0, x: 78, y: -28, rotate: 1.4, filter: "blur(8px)" }} animate={{ opacity: 1, x: 0, y: 0, rotate: 0, filter: "blur(0px)" }} exit={reduceMotion ? undefined : { opacity: 0, x: -60, y: 22, rotate: -1.2, filter: "blur(6px)" }} transition={{ duration: 0.33 }}>
                    <span className="eth-risk-v13-profile-label">{currentProfile.label}</span><h3>{currentProfile.title}</h3>
                    <div className="eth-risk-v13-profile-grid"><div><span>MAX BET</span><strong>{currentProfile.maxBet}</strong></div><div><span>SESSION</span><strong>{currentProfile.session}</strong></div><div><span>GEO</span><strong>{currentProfile.geo}</strong></div><div><span>VPN</span><strong>{currentProfile.vpn}</strong></div><div><span>EXCLUSION</span><strong>{currentProfile.exclusion}</strong></div></div>
                    <p>{currentProfile.note}</p>
                    <div className="eth-risk-v13-profile-foot"><Layers3 size={17} /><span>These examples show configuration choices; the operator remains responsible for approving the actual thresholds.</span></div>
                  </motion.article>
                </AnimatePresence>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="eth-risk-v13-section">
          <div className="eth-risk-v13-shell">
            <Reveal direction="topRight" className="eth-risk-v13-fairness">
              <div className="eth-risk-v13-fairness-copy"><span className="eth-risk-v13-kicker">RISK DECISIONS AND GAME OUTCOMES</span><h2>Exposure rules govern admission. <span>They do not rewrite the game result.</span></h2><p>A wager restriction may reject an attempted action, but it must not secretly change the outcome of an already admitted round. The separate fairness page explains the verification and replay mechanisms used by supported first-party games.</p><Link to="/provably-fair-engine" className="eth-risk-v13-inline-link">Explore game verification <ArrowRight size={15} /></Link></div>
              <div className="eth-risk-v13-fairness-flow"><div className="eth-risk-v13-fairness-block"><ShieldCheck size={22} /><span>ELIGIBILITY CHECK</span><strong>ALLOW / REJECT</strong><small>Prior to admission</small></div><div className="eth-risk-v13-fairness-separator"><ArrowRight size={20} /><span>ALLOWED</span></div><div className="eth-risk-v13-fairness-block eth-risk-v13-fairness-block--game"><Fingerprint size={22} /><span>GAME RESOLUTION</span><strong>RULES + RESULT</strong><small>As supported by each game</small></div></div>
            </Reveal>
          </div>
        </section>

        <section className="eth-risk-v13-section eth-risk-v13-section--panel" id="governance">
          <div className="eth-risk-v13-shell">
            <Reveal direction="topLeft" className="eth-risk-v13-section-head eth-risk-v13-section-head--split">
              <div><span className="eth-risk-v13-kicker">AUDIT LOGGING AND OPERATOR REVIEW</span><h2>A risk decision is only useful <span>if someone can investigate it.</span></h2></div>
              <p>The audit trail connects the important questions: who changed a limit, what account triggered an alert, when access was denied and which records informed a later review. The example events below illustrate the kinds of operational evidence an operator needs.</p>
            </Reveal>

            <div className="eth-risk-v13-governance-layout">
              <Reveal direction="left" className="eth-risk-v13-governance-events">
                <div className="eth-risk-v13-governance-events-head"><Activity size={18} /><div><span>EXAMPLE AUDIT ENTRIES</span><strong>Illustrative event history</strong></div><b>SAMPLE</b></div>
                {[
                  [Gauge, "Wager limit updated", "operator / game settings", "RECORDED"],
                  [MapPin, "Restricted-region access denied", "player / location check", "DENIED"],
                  [UserX, "Player exclusion requested", "account / eligibility", "RESTRICTED"],
                  [WifiOff, "Withdrawal flagged for review", "wallet / risk queue", "PENDING"],
                ].map((event) => { const Icon = event[0]; return <div className="eth-risk-v13-governance-event" key={event[1]}><span><Icon size={15} /></span><div><strong>{event[1]}</strong><small>{event[2]}</small></div><b>{event[3]}</b></div>; })}
              </Reveal>

              <Reveal direction="right" className="eth-risk-v13-governance-copy">
                <div className="eth-risk-v13-governance-card"><Settings2 size={22} /><div><span>POLICY CHANGE HISTORY</span><h3>Find out who changed a rule.</h3><p>Reviewers need the affected setting, the actor, the time of change and the relevant operator context. This helps separate an intentional adjustment from an unauthorized change or an accidental misconfiguration.</p></div></div>
                <div className="eth-risk-v13-governance-card"><FileCheck2 size={22} /><div><span>INVESTIGATION CONTEXT</span><h3>Connect alerts to the underlying activity.</h3><p>A suspicious session or withdrawal is easier to assess with linked player history, transaction references, security signals and an escalation trail. An audit log is most useful when it leads to the facts behind the alert.</p></div></div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="eth-risk-v13-section">
          <div className="eth-risk-v13-shell">
            <Reveal direction="bottomLeft" className="eth-risk-v13-section-head"><span className="eth-risk-v13-kicker">HOW RISK CONTROLS STAY RELIABLE</span><h2>Useful controls do more than warn. <span>They support a defensible decision.</span></h2><p>The following principles explain how Ethosino approaches authorization, signal quality, traceability and human oversight. They also clarify where automated checks should stop.</p></Reveal>
            <div className="eth-risk-v13-principles-grid">
              {principles.map((principle, index) => { const Icon = principle[0]; const dirs = ["topLeft", "topRight", "left", "right", "bottomLeft", "bottomRight"]; return <Reveal key={principle[1]} direction={dirs[index]} delay={index * 0.035}><Spotlight className="eth-risk-v13-principle-card"><div className="eth-risk-v13-principle-card-top"><span>0{index + 1}</span><Icon size={20} /></div><h3>{principle[1]}</h3><p>{principle[2]}</p></Spotlight></Reveal>; })}
            </div>
          </div>
        </section>

        <section className="eth-risk-v13-section eth-risk-v13-section--panel">
          <div className="eth-risk-v13-shell">
            <Reveal direction="bottomRight" className="eth-risk-v13-legal">
              <div className="eth-risk-v13-legal-icon"><FileCheck2 size={25} /></div>
              <div className="eth-risk-v13-legal-copy"><span>OPERATOR RESPONSIBILITY</span><h2>Risk tooling assists operations. <strong>It does not guarantee compliance.</strong></h2><p>Ethosino supplies access restrictions, monitoring and administrative review surfaces. Operators must establish their own market eligibility, regulatory approvals where applicable, identity-verification arrangements, responsible-gaming policies, and payment and fraud-response procedures. Technical safeguards require proper configuration and ongoing oversight.</p></div>
              <div className="eth-risk-v13-legal-points"><span><Check size={12} /> apply approved access rules</span><span><Check size={12} /> retain reviewable activity</span><span><X size={12} /> no automatic regulatory approval</span></div>
            </Reveal>
          </div>
        </section>

        <section className="eth-risk-v13-final">
          <div className="eth-risk-v13-shell">
            <Reveal direction="bottomLeft" className="eth-risk-v13-final-card">
              <div className="eth-risk-v13-final-grid" /><div className="eth-risk-v13-final-beam" />
              <div className="eth-risk-v13-final-copy"><span>FROM ALERT TO ACTION TO AUDIT TRAIL</span><h2>See how the operator <strong>handles a real risk scenario.</strong></h2><p>Walk through an over-limit wager, a location-denied account, a player exclusion or a flagged withdrawal. Examine the policy check, the administrative context and the evidence available to the reviewer rather than relying on a feature checklist.</p><div className="eth-risk-v13-final-actions"><Link to="/book-demo" className="eth-risk-v13-btn eth-risk-v13-btn--primary eth-risk-v13-btn--large">Book a risk operations walkthrough <ArrowRight size={18} /></Link><Link to="/request-documentation" className="eth-risk-v13-btn eth-risk-v13-btn--ghost eth-risk-v13-btn--large">Request risk documentation</Link></div></div>
              <div className="eth-risk-v13-final-checklist"><div className="eth-risk-v13-final-checklist-head"><ShieldCheck size={21} /><div><span>WHAT THE REVIEW COVERS</span><strong>Follow the decision and evidence.</strong></div></div><div className="eth-risk-v13-final-checklist-list"><span><Check size={13} /> Inspect a rejected over-limit wager</span><span><Check size={13} /> Follow a player exclusion request</span><span><Check size={13} /> Review a denied jurisdiction check</span><span><Check size={13} /> Investigate a network-risk indicator</span><span><Check size={13} /> Trace a flag or policy edit to the log</span></div></div>
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />
    </Fragment>
  );
};

export default RiskManagement;
